(() => {
  if (window.RiftHome) return;

  const supportsReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const oncePerElement = (element, key) => {
    if (!element) return false;
    const flag = `rift${key}`;
    if (element.dataset[flag] === '1') return false;
    element.dataset[flag] = '1';
    return true;
  };

  const observeOnce = (elements, { threshold = 0.5, rootMargin = '0px', onEnter }) => {
    if (!('IntersectionObserver' in window)) {
      elements.forEach((el) => onEnter(el));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          onEnter(entry.target);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin }
    );

    elements.forEach((el) => observer.observe(el));
  };

  const animateNumber = ({ element, from = 0, to, duration = 1000, format }) => {
    if (!element) return;

    if (supportsReducedMotion()) {
      element.textContent = format ? format(to) : String(to);
      return;
    }

    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const value = from + (to - from) * progress;
      const outputValue = Math.round(value);
      element.textContent = format ? format(outputValue) : String(outputValue);
      if (progress < 1) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  };

  const formatInteger = (value) => {
    try {
      return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
    } catch {
      return String(value);
    }
  };

  const initImageCardCounterAndRotation = (root) => {
    const cards = Array.from(root.querySelectorAll('.image-card'));
    if (!cards.length) return;

    observeOnce(cards, {
      threshold: 0.5,
      onEnter: (card) => {
        if (!oncePerElement(card, 'ImageCardInit')) return;

        const svg = card.querySelector('svg');
        if (svg && !svg.classList.contains('rotate-on-visible')) {
          svg.classList.add('rotate-on-visible');
        }

        const countEl = card.querySelector('.metric-number');
        if (countEl) {
          const currentTextNumber = parseInt(String(countEl.textContent || '').replace(/[^0-9]/g, ''), 10);
          const hasStartedCounting = Number.isFinite(currentTextNumber) && currentTextNumber > 0;
          if (hasStartedCounting) return;

          const endVal = parseInt(countEl.dataset.countEnd, 10);
          if (!Number.isFinite(endVal)) return;

          const suffix = countEl.dataset.countSuffix || '';

          if (window.RiftCounters && typeof window.RiftCounters.animate === 'function') {
            window.RiftCounters.animate(countEl, {
              endVal,
              duration: 2500,
              suffix
            });
          } else {
            animateNumber({
              element: countEl,
              to: endVal,
              duration: 2500,
              format: (v) => `${formatInteger(v)}${suffix}`
            });
          }
        }
      }
    });
  };

  const initSpinGauge = (root) => {
    const gauges = Array.from(root.querySelectorAll('.spin-gauge-overlay'));
    if (!gauges.length) return;

    observeOnce(gauges, {
      threshold: 0.6,
      onEnter: (gauge) => {
        if (!oncePerElement(gauge, 'SpinGaugeInit')) return;

        const gaugeCircle = gauge.querySelector('.gauge-circle');
        const rpmText = gauge.querySelector('#rpmValue');

        if (gaugeCircle) {
          setTimeout(() => {
            gaugeCircle.style.background =
              'conic-gradient(#4ade80 290deg, #3f3f46 290deg 360deg)';
          }, 100);
        }

        animateNumber({
          element: rpmText,
          to: 1850,
          duration: 1200
        });
      }
    });
  };

  const initSpeedGauge = (root) => {
    const gauges = Array.from(root.querySelectorAll('.speed-gauge-overlay'));
    if (!gauges.length) return;

    observeOnce(gauges, {
      threshold: 0.6,
      onEnter: (gauge) => {
        if (!oncePerElement(gauge, 'SpeedGaugeInit')) return;

        const lines = gauge.querySelector('.lines');
        const centerLine = gauge.querySelector('.center-line');
        const speedText = gauge.querySelector('#speedValue');

        if (lines) {
          lines.style.transform = 'translateX(0)';
          lines.style.opacity = '1';
        }

        if (centerLine) {
          centerLine.style.transform = 'translateX(-50%) translateX(0)';
          centerLine.style.opacity = '1';
        }

        animateNumber({
          element: speedText,
          to: 57,
          duration: 1000,
          format: (v) => `${v} MPH`
        });
      }
    });
  };

  const initPulseText = (root) => {
    const pulseText = root.getElementById ? root.getElementById('pulse-text') : null;
    if (!pulseText) return;
    if (!oncePerElement(pulseText, 'PulseTextInit')) return;

    const lines = pulseText.querySelectorAll('.text-line');
    if (!lines || lines.length < 2) return;

    const textSteps = [
      ['EDGE MISHITS', '7%'],
      ['OFF-CENTER', '23%'],
      ['SWEET SPOT', '61%'],
      ['MISSED SHOT', '9%']
    ];

    let currentIndex = 0;
    let intervalId = null;

    const setStep = (index) => {
      const [label, value] = textSteps[index];
      lines[0].textContent = label;
      lines[1].textContent = value;
    };

    const start = () => {
      if (intervalId) return;
      intervalId = window.setInterval(() => {
        currentIndex = (currentIndex + 1) % textSteps.length;
        setStep(currentIndex);
      }, 1500);
    };

    const stop = () => {
      if (!intervalId) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    setStep(currentIndex);

    if (!('IntersectionObserver' in window) || supportsReducedMotion()) {
      start();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) start();
          else stop();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(pulseText);
  };

  const initPaddleSlider = (root) => {
    const slider = root.querySelector('.paddle-slider-inner');
    if (!slider) return;
    if (!oncePerElement(slider, 'PaddleSliderInit')) return;

    const dots = Array.from(root.querySelectorAll('.paddle-dot'));
    if (!dots.length) return;

    let currentIndex = 0;

    const updateSlider = (index) => {
      slider.style.transform = `translateX(-${index * 100}%)`;
      dots.forEach((d) => d.classList.remove('active'));
      if (dots[index]) dots[index].classList.add('active');
      currentIndex = index;
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.getAttribute('data-index') || '0', 10);
        if (!Number.isFinite(index)) return;
        updateSlider(index);
      });
    });

    let startX = 0;
    let isSwiping = false;

    slider.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        isSwiping = true;
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchmove',
      (e) => {
        if (!isSwiping) return;
        const currentX = e.touches[0].clientX;
        const diff = startX - currentX;

        if (Math.abs(diff) > 50) {
          if (diff > 0 && currentIndex < dots.length - 1) updateSlider(currentIndex + 1);
          else if (diff < 0 && currentIndex > 0) updateSlider(currentIndex - 1);
          isSwiping = false;
        }
      },
      { passive: true }
    );

    slider.addEventListener('touchend', () => {
      isSwiping = false;
    });
  };

  const initTestimonialsCentering = (root) => {
    const container = root.querySelector('.testimonial-scroll-container');
    if (!container) return;
    if (!oncePerElement(container, 'TestimonialsCenterInit')) return;

    const center = (container.scrollWidth - container.clientWidth) / 2;
    if (center > 0) container.scrollLeft = center;
  };

  const initDeferredAutoplayVideos = (root) => {
    if (supportsReducedMotion()) return;

    const wrappers = Array.from(root.querySelectorAll('video-media[data-defer-autoplay]'));
    if (!wrappers.length) return;

    const playDeferredAutoplayVideos = () => {
      const allWrappers = Array.from(document.querySelectorAll('video-media[data-defer-autoplay]'));

      for (const wrapper of allWrappers) {
        if (!wrapper || wrapper.hasAttribute('playing')) continue;

        try {
          // Delegate to the theme's <video-media> custom element so it can handle
          // autoplay-policy failures ("suspended" state, controls fallback, etc.).
          wrapper.play();
        } catch {
          // no-op
        }
      }
    };

    const scheduleIdlePlay = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(playDeferredAutoplayVideos, { timeout: 2500 });
      } else {
        setTimeout(playDeferredAutoplayVideos, 1500);
      }
    };

    // Set up global listeners only once, but allow this init() to be called on
    // section reloads (theme editor) without missing newly-inserted wrappers.
    if (!window.__riftDeferredAutoplayVideosListenersInit) {
      window.__riftDeferredAutoplayVideosListenersInit = true;

      window.addEventListener('load', scheduleIdlePlay, { once: true });
      window.addEventListener('pointerdown', playDeferredAutoplayVideos, { once: true, passive: true });
      window.addEventListener('keydown', playDeferredAutoplayVideos, { once: true });
      window.addEventListener('touchstart', playDeferredAutoplayVideos, { once: true, passive: true });
    }
  };

  const init = (root = document) => {
    try {
      initDeferredAutoplayVideos(root);
      initImageCardCounterAndRotation(root);
      initSpinGauge(root);
      initSpeedGauge(root);
      initPulseText(root);
      initPaddleSlider(root);
      initTestimonialsCentering(root);
    } catch (e) {
      // Avoid breaking the page if a single widget fails.
      // eslint-disable-next-line no-console
      console.warn('[RiftHome] init error', e);
    }
  };

  window.RiftHome = { init };

  const run = () => init(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();

  document.addEventListener('shopify:section:load', (event) => {
    if (!event || !event.target) return;
    init(event.target);
  });
})();
